/**
 * Central prompt details component
 */
//  Generic imports
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

//  Motif imports
import { MotifButton, MotifToast } from "@ey-xd/motif-react";
import { MotifIconButton } from "@ey-xd/motif-react/Button";

//  Icons imports
import iconArrowBack from "../../../assets/images/arrow_back.svg";
import iconCheckCircleSuccess from '../../../assets/images/check-circle.svg';

//  Redux imports
import { setTableTypes as setTableTypesToStore, setSelectedCentralPrompt } from "../../../redux/promptSlice";

//  Internal imports
import "./CentralPromptDetails.scss";
import LoaderComponent from '../../molecules/loader/LoaderComponent';
import PromptLibraryService from '../../../services/promptLibraryService';
import CopyCentralModal from '../../molecules/modals/copyCentralPrompt/CopyCentralModal';
import EditCentralPrompt from "../../molecules/forms/editCentralPrompt/EditCentralPrompt";
import CommonService from "../../../services/commonService";

function CentralPromptDetails() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { user } = useSelector(state => state.user);
    const { selectedCentralPrompt } = useSelector(state => state.prompt);
    const [centralPrompt, setCentralPrompt] = useState();
    const [isLoadingInProgress, setIsLoadingInProgress] = useState(false);
    const [showCopyToPersonalModal, setShowCopyToPersonalModal] = useState(false);
    const [showEditCentralPrompt, setShowEditCentralPrompt] = useState(false);
    const [showPromptSaveSuccessToast, setShowPromptSaveSuccessToast] = useState(false);
    const [tableTypeRefs, setTableTypeRefs] = useState([]);

    const promptLibraryService = new PromptLibraryService();
    const commonService = new CommonService();

    async function getPrompt(vPrompt) {
        let responsePrompt = {};
        const vData = {
            Category: vPrompt.api,
            Type: "A",
            ScopeId: ""
        };
        responsePrompt = await promptLibraryService.getCentralPrompt(vData);
        setCentralPrompt(responsePrompt);
    };

    useEffect(() => {
        setIsLoadingInProgress(true);
        if (selectedCentralPrompt) {
            getPrompt(selectedCentralPrompt);
            setIsLoadingInProgress(false);
        }
        // eslint-disable-next-line
    }, [id])

    /**
     * Hook to populate table type references
     */
    useEffect(() => {
        const getReferences = async () => {
            const response = await commonService.getReferences();
            if (response) {
                const vTableTypeRefs = response.filter(element => (element.GroupKey === "TableType"));
                setTableTypeRefs(vTableTypeRefs);
            }
        };
        getReferences();
        // eslint-disable-next-line
    }, []);

     /**
    * Function called when copy to personal button clicked
    */
    const onCopyToPersonalClick = () => {  
        setShowCopyToPersonalModal(true);
    }

    /**
     * Function to close a copy to personal modal.
     */ 
    const handleCloseModal = () => {
        setShowCopyToPersonalModal(false);

    }

    /**
     * Function to add prompt to personal from central.
     */
    const handleConfirm= async (promptName) => {
        if (selectedCentralPrompt && promptName) {
            const vData = {
                CentralPromptname: selectedCentralPrompt.promptName,
                PersonalPromptname: promptName,
                TableType: selectedCentralPrompt?.tableType,
                createdBy: user.username
            };
            const response = await promptLibraryService.copyCentralPromptToPersonal(vData);
            if (response.status === 200) {
                setShowCopyToPersonalModal(false);
            }  
        }
    };

    /**
     * Function to get Central prompts associated with user
     */
    const getTableTypes = async (userId) => {
        const vData = { userId: userId, promptType: "central" };
        const vTableTypes = await promptLibraryService.getTableTypes(vData);
        dispatch(setTableTypesToStore({
            tableTypes: vTableTypes
        }));
    }

    const handleSavePrompt = async (updatedPrompt) => {
        setIsLoadingInProgress(true);
        const response = await promptLibraryService.saveCentralPrompt(updatedPrompt);
        if(user) {
            getTableTypes(user.username);
        }
        setIsLoadingInProgress(false);
        setShowEditCentralPrompt(false);
        if(response?.status === 200) {
            getPrompt(updatedPrompt);
            setShowPromptSaveSuccessToast(true);
        }
    }

    const handleBack = () => {
        dispatch(setSelectedCentralPrompt( selectedCentralPrompt ));
        navigate(-1);
    }

    return (
        <React.Fragment>
            <LoaderComponent show={isLoadingInProgress} />
            <div className="prompt-details">
                <div className="prompt-div">
                    <MotifIconButton
                        aria-label="Click here to go back to Central Prompt Library"
                        onClick={() => handleBack()}
                        data-testid="testIdBtnBack"
                    >
                        <img src={iconArrowBack} alt='Go to previous page' />
                    </MotifIconButton>
                    <span className='prompt-name'>{selectedCentralPrompt?.tableType}</span>
                </div>
                <div className="prompt-container">
                {
                    showEditCentralPrompt ? (
                        <div className="view-edit-prompt">
                            <EditCentralPrompt 
                                centralPrompt={centralPrompt}
                                setShowEditCentralPrompt={setShowEditCentralPrompt}
                                handlePromptSave={handleSavePrompt}
                                tableTypeRefs={tableTypeRefs} />
                        </div>
                    ):(
                        <React.Fragment>
                            <div className="view-edit-prompt">
                                <div className="field-label">Prompt Name</div>
                                <div className="field-value">{selectedCentralPrompt?.promptName}</div>
                                    {
                                        centralPrompt?.map((prompt) => (
                                            <React.Fragment key={prompt.platformPromptId}>
                                                <div className="field-label">{prompt.platformPromptTitle}</div>
                                                <div className="field-value">{prompt.platformPromptText}</div>
                                            </React.Fragment>
                                        ))
                                    }                        
                                </div>

                               
                            {
                                showPromptSaveSuccessToast &&
                                <MotifToast
                                    variant="success"
                                    onClose={() => {
                                        setShowPromptSaveSuccessToast(false);
                                    }}
                                    icons={{toast:<img src={iconCheckCircleSuccess} alt="Success" />}}
                                    className='notifyToast'
                                >
                                    Prompt saved successfully
                                </MotifToast>
                            }
                               
                                
                            <div className='genericFooter'>
                                <div className='footerInner alignEnd'>
                                    <MotifButton onClick={() => onCopyToPersonalClick()} className='' type="button" variant="secondary">
                                        Copy to Personal
                                    </MotifButton>
                                </div>
                            </div>
                            {showCopyToPersonalModal && (
                                <CopyCentralModal
                                    onClose={handleCloseModal}
                                    handleConfirm={handleConfirm}
                                />
                            )}
                        </React.Fragment>
                    )
                }
                </div>
            </div>
        </React.Fragment>
    );
}

export default CentralPromptDetails;